import { z } from 'zod'

import { TransportTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { Transfer } from '../../../lib/types/models/transfer/transfer'
import { TransferInput } from '../../../lib/types/models/transfer/transfer-input'
import { DepartureTimeTripProviderInput } from '../../../lib/types/models/trip-booking/departure-time-trip-provider'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripDate } from '../../../lib/types/models/trip-booking/trip-date'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import {
  createDateStringSchema,
  createTimeSchema
} from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const mapTripBookingToInput = (
  tripBooking: TripBookingTypes.Light
): TripBookingTypes.Input => ({
  isTakeInCharge: tripBooking.isTakeInCharge,
  travelCompany: tripBooking.travelCompany?.name,
  directionality: tripBooking.directionality,
  numberOfStaffPlacesReserved: tripBooking.numberOfStaffPlacesReserved,
  numberOfStudentsPlacesReserved: tripBooking.numberOfStudentsPlacesReserved,
  relatedTripId: tripBooking.relatedTrip.id,
  mainReservationId: tripBooking.mainReservationId,
  ...(tripBooking.isTakeInCharge
    ? {
        date: tripBooking?.date,
        label: tripBooking?.label,
        meetingPlaceId: tripBooking?.meetingPlace?.id,
        meetingTimeForParents: tripBooking?.meetingTimeForParents,
        meetingTimeForStaff: tripBooking?.meetingTimeForStaff
      }
    : {
        lecResponsableId: tripBooking.lecResponsable?.id,
        reservationNumber: tripBooking.reservationNumber,
        totalPrice: tripBooking.totalPrice,
        transportMeansId: tripBooking.transportMeans?.id,
        tripProviderId: tripBooking.tripProvider?.id
      })
})

export const mapTripStepToInput = (tripStep: TripStepTypes.Full): TripStepTypes.Input => ({
  departureDate: tripStep.departureDate,
  arrivalDate: tripStep.arrivalDate,
  arrivalTime: tripStep.arrivalTime,
  departurePlaceId: tripStep.departurePlaceId,
  departureTime: tripStep.departureTime,
  arrivalPlaceId: tripStep.arrivalPlaceId,
  arrivalTimeZone: tripStep.arrivalTimeZone,
  carNumber: tripStep.carNumber,
  timeBeforeDepartureForLEC: tripStep.timeBeforeDepartureForLEC,
  timeBeforeDepartureForParents: tripStep.timeBeforeDepartureForParents,
  timeBeforeDepartureForStaff: tripStep.timeBeforeDepartureForStaff,
  timeBeforeDepartureForGroup: tripStep.timeBeforeDepartureForGroup,
  timeBeforeArrivalForLEC: tripStep.timeBeforeArrivalForLEC,
  timeBeforeArrivalForParents: tripStep.timeBeforeArrivalForParents,
  foreignProviderAppointmentTime: tripStep.foreignProviderAppointmentTime,
  foreignProviderAppointmentPlaceId: tripStep.foreignProviderAppointmentPlaceId,
  departureTimeZone: tripStep.departureTimeZone,
  olGuidelineContent: tripStep.olGuidelineContent,
  olGuidelineId: tripStep.olGuidelineId,
  transportMeanNumber: tripStep.transportMeanNumber,
  tripProviderGuidelineContent: tripStep.tripProviderGuidelineContent,
  tripProviderGuidelineId: tripStep.tripProviderGuidelineId,
  departureTimeTripProviders: tripStep.departureTimeTripProviders.map(
    (departureTimeTripProvider) => ({
      tripProviderId: departureTimeTripProvider.tripProviderId,
      label: departureTimeTripProvider.label,
      meetingPlace: departureTimeTripProvider.meetingPlace,
      time: departureTimeTripProvider.time
    })
  )
})

export const mapTripStepInputToRequest = (
  input: TripStepTypes.Input,
  type: TripStepTypeEnum
): TripStepTypes.Request => {
  return {
    type,
    ...input,
    departureTimeTripProviders:
      input.departureTimeTripProviders as DepartureTimeTripProviderInput[]
  }
}

export const mapTransferToInput = (transfer: Transfer): TransferInput => ({
  arrivalPlaceId: transfer.arrivalPlaceId,
  arrivalTime: transfer.arrivalTime,
  departurePlaceId: transfer.departurePlaceId,
  departureTime: transfer.departureTime,
  transportMeansId: transfer.transportMeansId,
  tripProviderId: transfer.tripProviderId,
  numberOfStaff: transfer.numberOfStaff,
  numberOfStudents: transfer.numberOfStudents,
  totalPrice: transfer.totalPrice,
  transferDate: transfer.transferDate,
  reservationNumber: transfer.reservationNumber,
  departureTimeLEC: transfer.departureTimeLEC,
  departureTimeProvider: transfer.departureTimeProvider,
  lecResponsableId: transfer.lecResponsableId,
  travelCompany: transfer.travelCompany?.name,
  travelCompanyId: transfer.travelCompanyId,
  tripProviderGuidelineContent: transfer.tripProviderGuidelineContent,
  tripProviderGuidelineId: transfer.tripProviderGuidelineId
})

export type TripStepMode = 'departure' | 'arrival'

type PersonTime =
  | 'LEC_DP'
  | 'PARENTS_DP'
  | 'STAFF_DP'
  | 'GROUP_DP'
  | 'LEC_AR'
  | 'PARENTS_AR'
  | 'FOREIGN_PROVIDER_AR'

type TimeBeforeMapping = {
  label: string
  name: keyof TripStepTypes.Input
  mode: 'departure' | 'arrival'
  time: 'departure' | 'arrival'
}

export const timeBeforeMapping: Record<PersonTime, TimeBeforeMapping> = {
  LEC_DP: {
    label: 'Heure de RDV LEC/AAT',
    name: 'timeBeforeDepartureForLEC',
    mode: 'departure',
    time: 'departure'
  },
  STAFF_DP: {
    label: 'Heure de RDV encadrants',
    name: 'timeBeforeDepartureForStaff',
    mode: 'departure',
    time: 'departure'
  },
  PARENTS_DP: {
    label: 'Heure de RDV des parents',
    name: 'timeBeforeDepartureForParents',
    mode: 'departure',
    time: 'departure'
  },
  GROUP_DP: {
    label: 'Heure de RDV groupe/AAT',
    name: 'timeBeforeDepartureForGroup',
    mode: 'arrival',
    time: 'departure'
  },
  LEC_AR: {
    label: 'Heure de RDV LEC/AAT',
    name: 'timeBeforeArrivalForLEC',
    mode: 'arrival',
    time: 'arrival'
  },
  PARENTS_AR: {
    label: 'Heure de RDV parents',
    name: 'timeBeforeArrivalForParents',
    mode: 'arrival',
    time: 'arrival'
  },
  FOREIGN_PROVIDER_AR: {
    label: 'Heure de RDV du prestataire étranger',
    name: 'foreignProviderAppointmentTime',
    mode: 'departure',
    time: 'arrival'
  }
}

export const getDpTripStep = (mode: TripStepMode): TimeBeforeMapping[] => {
  return Object.values(timeBeforeMapping).filter(
    ({ mode: m, time }) => m === mode && time === 'departure'
  )
}

export const getAtTripStep = (mode: TripStepMode): TimeBeforeMapping[] => {
  return Object.values(timeBeforeMapping).filter(
    ({ mode: m, time }) => m === mode && time === 'arrival'
  )
}

const baseReservationSchema = z.object({
  directionality: createEnumSchema({ enumType: TransportTypeEnum, fieldName: 'directionality' }),
  relatedTripId: createNumberSchema('Voyage', { isInteger: true, isPositive: true }),
  isTakeInCharge: z.boolean().default(false),
  numberOfStudentsPlacesReserved: createNumberSchema('Nombre de jeunes', {
    isInteger: true,
    isPositive: true
  })
})

const isTakeInChargeFalseSchema = z.object({
  travelCompany: createStringSchema('Compagnie', { nullish: true }),
  lecResponsableId: createNumberSchema('Responsable LEC', {
    isInteger: true,
    isPositive: true
  }),
  transportMeansId: createNumberSchema('Moyen de transport', {
    isInteger: true,
    isPositive: true
  }),
  tripProviderId: createNumberSchema('Prestataire voyage', {
    isInteger: true,
    isPositive: true
  }),
  reservationNumber: createStringSchema('Numéro de réservation', {
    errorMessage: 'Veuillez saisir Numéro de réservation valide'
  }),
  totalPrice: createNumberSchema('Prix (€)'),
  mainReservationId: createNumberSchema('Réservation mère', {
    isInteger: true,
    isPositive: true,
    nullish: true
  }),
  numberOfStaffPlacesReserved: createNumberSchema(`Nombre d'encadrants`, {
    isInteger: true,
    isPositive: true,
    minimumValue: 1
  })
})

const isTakeInChargeTrueSchema = z.object({
  label: createStringSchema('Libellé'),
  date: createDateStringSchema({ inputName: 'Date' }),
  meetingPlaceId: createNumberSchema('Lieu de rendez-vous', { isInteger: true, isPositive: true }),
  meetingTimeForParents: createTimeSchema({ inputName: 'Heure de rendez-vous des parents' }),
  meetingTimeForStaff: createTimeSchema({
    inputName: 'Heure de rendez-vous des encadrants'
  }).nullish(),
  numberOfStaffPlacesReserved: createNumberSchema(`Nombre d'encadrants`, {
    isInteger: true,
    optional: true
  })
})

export const TRIP_BOOKING_FORM_SCHEMA_VALIDATION = z.discriminatedUnion('isTakeInCharge', [
  baseReservationSchema
    .extend({ isTakeInCharge: z.literal(false) })
    .merge(isTakeInChargeFalseSchema),

  baseReservationSchema.extend({ isTakeInCharge: z.literal(true) }).merge(isTakeInChargeTrueSchema)
])

export function validateSelectedTripDates(selectedTripDates: TripDate[]): {
  valid: boolean
  error?: string
} {
  if (selectedTripDates.length === 0) {
    return { valid: false, error: 'Aucun voyage sélectionné' }
  }
  const [first] = selectedTripDates

  const validations: { check: boolean; error: string }[] = [
    {
      check: selectedTripDates.every((tripDate) => tripDate.date === first.date),
      error: 'Les dates des voyages doivent être identiques'
    },
    {
      check: selectedTripDates.every(
        (tripDate) => tripDate.directionality === first.directionality
      ),
      error: 'Les types de trajets doivent être identiques'
    },
    {
      check: selectedTripDates.every((tripDate) => tripDate.trip.type === first.trip.type),
      error: 'Les types de voyage doivent être identiques'
    }
  ]

  const failed = validations.find((v) => !v.check)
  if (failed) {
    return { valid: false, error: failed.error }
  }
  return { valid: true }
}
